import { Button } from '@bifeai/ui';
import { CircleHelp, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { setHelp, startTour, useHelp } from '../lib/help';
import { useSession } from '../lib/session';

/** Un paso del recorrido: si `target` no está en pantalla, el paso se muestra centrado. */
interface TourStep {
  target?: string;
  title: string;
  text: string;
}
interface TourDef {
  title: string;
  /** Pantalla a la que lleva antes de empezar. */
  path: string;
  steps: TourStep[];
}

/** Recorridos guiados. Los selectores apuntan a partes estables de cada pantalla. */
export const TOURS: Record<string, TourDef> = {
  general: {
    title: 'Recorrido general',
    path: '/',
    steps: [
      {
        title: 'Bienvenido a BifeAI',
        text: 'En un minuto te mostramos dónde está cada cosa. Podés salir cuando quieras y volver a verlo desde tu menú.',
      },
      {
        target: '.bf-sidebar-nav',
        title: 'El menú',
        text: 'Todas las secciones del día a día: mostrador, caja, ventas, mercadería, desposte, empleados y estadísticas.',
      },
      { target: '.bf-picker', title: 'Sucursal', text: 'Con varias sucursales, acá elegís cuál mirar o “Todas” para ver la carnicería completa.' },
      {
        target: '[aria-label^="Alertas"]',
        title: 'Alertas',
        text: 'La campanita: vencimientos, stock bajo, pagos a proveedores y deudas atrasadas. El número rojo dice cuántas faltan leer; en “Ver todas” está el listado completo.',
      },
      {
        target: '[data-tour="user-menu"]',
        title: 'Tu menú',
        text: 'Configuración, sucursales, usuarios, tu cuenta, las ayudas y cerrar sesión están acá.',
      },
      {
        target: '[data-tour="setup"]',
        title: 'Puesta en marcha',
        text: 'Los pasos que faltan para operar con todo. El botón de cada uno te lleva a hacerlo. Se puede minimizar o cerrar.',
      },
      {
        target: '[data-tour="help"]',
        title: 'Ayuda de cada pantalla',
        text: 'En las pantallas principales, este botón “?” te muestra cómo se usa esa pantalla.',
      },
    ],
  },
  pos: {
    title: 'Punto de venta',
    path: '/venta',
    steps: [
      { title: 'Punto de venta', text: 'Desde acá se vende. Primero se abre la caja con el efectivo del cajón.' },
      {
        target: '.bf-pos-scan',
        title: 'Escanear o buscar',
        text: 'Pasá el lector por la etiqueta de la balanza o el código de barras, o escribí el nombre o el PLU.',
      },
      {
        target: '.bf-pos-catalog',
        title: 'Productos',
        text: 'También se puede tocar el producto. Los que van por peso piden los kilos (o los toma de la balanza conectada).',
      },
      {
        target: '.bf-pos-ticket',
        title: 'El ticket',
        text: 'Lo que se va cargando. Tocá un renglón para cambiar la cantidad o quitarlo. Arriba: abrir cajón, cambiar de cajero y entrada/salida.',
      },
      {
        target: '.bf-pos-pay',
        title: 'Cobrar',
        text: 'Con F2 o este botón se elige cómo paga: efectivo (calcula el vuelto), débito o crédito con cuotas, Mercado Pago (QR o Point), Cuenta DNI, transferencia, QR o fiado. Se puede pagar con dos medios.',
      },
      {
        title: 'Mercado Pago',
        text: 'Con la cuenta conectada: “Mercado Pago QR” muestra un QR con el importe exacto y “Mercado Pago Point” manda el importe al posnet. La venta se cierra sola cuando Mercado Pago aprueba el pago. En transferencias, “Verificar en Mercado Pago” confirma que entró la plata.',
      },
      {
        title: 'Tarjetas y billeteras',
        text: 'Con el posnet del banco o Cuenta DNI: cobrá en el aparato el mismo importe y confirmá en BifeAI cuando diga “Aprobado”. Así cada cobro queda separado y al cerrar la caja se compara con el cierre de lote del posnet.',
      },
    ],
  },
  cash: {
    title: 'Caja',
    path: '/caja',
    steps: [
      { title: 'Caja', text: 'El turno de caja: cuánto efectivo tendría que haber, ventas por medio de pago y movimientos.' },
      {
        target: '.bf-kpi-grid',
        title: 'Efectivo esperado',
        text: 'Fondo inicial + ventas en efectivo + cobros − retiros. Es lo que tendría que haber en el cajón.',
      },
      {
        target: '.bf-page-actions',
        title: 'Ingresos, retiros y cierre',
        text: 'Registrá retiros (pago a un repartidor, retiro del dueño) y al final del turno cerrá la caja contando el efectivo.',
      },
      {
        target: '.bf-cards-check',
        title: 'Control con el posnet',
        text: 'Total de débito y crédito del turno (por posnet si tenés varios). Comparalo con el cierre de lote que imprime el posnet al final del día: tienen que coincidir.',
      },
      {
        target: '.bf-table-wrap',
        title: 'Tickets y cajas anteriores',
        text: 'Tocá un ticket para reimprimirlo o anularlo mientras la caja siga abierta.',
      },
    ],
  },
  products: {
    title: 'Productos',
    path: '/productos',
    steps: [
      { title: 'Productos', text: 'El catálogo: cortes, elaborados, almacén e insumos, con su precio y su código de balanza.' },
      {
        target: '.bf-page-actions',
        title: 'Agregar o importar',
        text: 'Sumá productos de a uno, importalos desde una planilla o administrá las categorías.',
      },
      {
        target: '.bf-product-grid, .bf-table-wrap',
        title: 'La lista',
        text: 'Tocá un producto para editarlo: precio, PLU, vencimiento, foto y, con facturación, el IVA.',
      },
    ],
  },
  purchases: {
    title: 'Compras',
    path: '/compras',
    steps: [
      { title: 'Compras', text: 'Cada compra a un proveedor suma stock con su costo y su vencimiento, y queda en la cuenta del proveedor.' },
      {
        target: '.bf-page-actions',
        title: 'Nueva compra',
        text: 'Cargá el proveedor, los productos, kilos y costo. Las medias reses quedan listas para despostar.',
      },
      { target: '.bf-table-wrap', title: 'Historial', text: 'Las compras del período. Tocá una para ver el detalle.' },
    ],
  },
  cutting: {
    title: 'Desposte',
    path: '/desposte',
    steps: [
      { title: 'Desposte', text: 'Convertí una media res en cortes: cuánto salió de cada uno, el rinde y el costo por kilo.' },
      {
        target: '.bf-tabs',
        title: 'Secciones',
        text: 'Despostar, los tipos de media res con sus cortes, el historial y (con el adicional) el rinde por proveedor.',
      },
      {
        target: '.bf-page-actions',
        title: 'Empezar',
        text: 'Elegí la media res comprada, cargá los kilos de cada corte y tocá “Cargar cortes al stock”.',
      },
    ],
  },
  settings: {
    title: 'Configuración',
    path: '/configuracion',
    steps: [
      { title: 'Configuración', text: 'Todo lo del mostrador se ajusta acá. Cada pestaña explica lo suyo y se prueba antes de usarlo.' },
      {
        target: '.bf-tabs',
        title: 'Pestañas',
        text: 'Guía (cómo armar el mostrador), Esta caja (balanza y cajón de esta computadora), Balanza (etiquetas), Ticket, Pagos (medios de pago y posnets), Facturación, Vencimientos y Equipo.',
      },
      { target: '.bf-page-actions', title: 'Guardar', text: 'Los cambios se guardan con este botón y las cajas los toman al instante.' },
    ],
  },
  dashboard: {
    title: 'Dashboard',
    path: '/',
    steps: [
      { title: 'Dashboard', text: 'Cómo viene la carnicería en el período elegido, comparado con el período anterior.' },
      { target: '.bf-kpi-grid', title: 'Números principales', text: 'Ventas, tickets, kilos y ganancia (o ticket promedio).' },
      {
        target: '.bf-channel-grid',
        title: '¿Cómo te pagan?',
        text: 'Cuánto entró por cada medio: efectivo, tarjeta del banco, Mercado Pago, Cuenta DNI, transferencia, otras billeteras y fiado. El porcentaje muestra con cuál se cobra más.',
      },
    ],
  },
  fiscal: {
    title: 'Facturación ARCA',
    path: '/configuracion?tab=fiscal',
    steps: [
      { title: 'Facturación electrónica', text: 'Seis pasos para facturar con tu CUIT. Cada uno se marca “Hecho” al completarlo.' },
      {
        target: '.bf-tabs',
        title: 'Configuración',
        text: 'Todo lo del sistema se configura desde estas pestañas: balanza, ticket, pagos, facturación y más.',
      },
    ],
  },
  franchises: {
    title: 'Franquicias',
    path: '/franquicias',
    steps: [
      {
        title: 'Tu red de franquicias',
        text: 'Cada franquicia es otra empresa: cobra con su Mercado Pago, factura con su CUIT y la plata es suya. Acá ves sus números y manejás lo que define la marca, separado de tus locales propios.',
      },
      {
        target: '.bf-tabs',
        title: 'Cinco pestañas',
        text: 'Resumen (ventas, cobros, facturación y regalías), Reglas (precios y de dónde saca la mercadería), Pedidos y envíos, Cuenta corriente y Regalías.',
      },
      {
        title: 'Reglas de cada franquicia',
        text: 'Precios de la marca, propios o con margen; mercadería de la marca sin cargo o vendida a cuenta; proveedores homologados o propios; y el % de regalías.',
      },
      {
        title: 'Envíos y cuenta corriente',
        text: 'La franquicia pide, vos enviás desde uno de tus locales. Si se la vendés, el envío se carga a su cuenta corriente; los pagos los registrás vos.',
      },
      {
        title: 'Regalías',
        text: 'Cuando termina el mes, cargás las regalías a la cuenta de cada franquicia con un botón. Salen de las mismas ventas que ves en el Resumen.',
      },
    ],
  },
  myFranchise: {
    title: 'Mi franquicia',
    path: '/mi-franquicia',
    steps: [
      {
        title: 'Tu relación con la marca',
        text: 'Acá ves las reglas que puso la marca, tus pedidos de mercadería, tu cuenta corriente y tus regalías.',
      },
      {
        target: '.bf-kpi-grid',
        title: 'Tus números del mes',
        text: 'Ventas, regalías que corresponden, saldo con la marca y pedidos que todavía no te enviaron.',
      },
      {
        title: 'Tus cuentas',
        text: 'Tu Mercado Pago y tu facturación ARCA los conectás vos, con tus claves, desde Cuentas de la franquicia en tu menú.',
      },
    ],
  },
};

/** Recorrido que corresponde a la pantalla actual (para el botón "?"). */
export function tourForPath(pathname: string, search: string): string | null {
  if (pathname === '/venta') return 'pos';
  if (pathname === '/caja') return 'cash';
  if (pathname === '/productos') return 'products';
  if (pathname === '/compras') return 'purchases';
  if (pathname === '/desposte') return 'cutting';
  if (pathname === '/configuracion') return search.includes('tab=fiscal') ? 'fiscal' : 'settings';
  if (pathname === '/franquicias') return 'franchises';
  if (pathname === '/mi-franquicia') return 'myFranchise';
  if (pathname === '/') return 'dashboard';
  return null;
}

/** Botón "?" de la barra de arriba: abre el recorrido de la pantalla. */
export function HelpButton() {
  const { me } = useSession();
  const help = useHelp(me.user.id);
  const { pathname, search } = useLocation();
  const id = tourForPath(pathname, search);
  if (!help.tips || !id) return null;
  return (
    <button
      type="button"
      className="bf-icon-btn"
      aria-label={`Cómo se usa: ${TOURS[id]!.title}`}
      title="Cómo se usa esta pantalla"
      data-tour="help"
      onClick={() => startTour(id)}
    >
      <CircleHelp />
    </button>
  );
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Motor del recorrido: oscurece la pantalla, resalta un elemento y muestra la explicación. */
export function TourHost() {
  const { me, can } = useSession();
  const help = useHelp(me.user.id);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [tour, setTour] = useState<{ id: string; i: number } | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    const onStart = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      const def = TOURS[id];
      if (!def) return;
      const [path] = def.path.split('?');
      if (pathname !== path) navigate(def.path);
      setTour({ id, i: 0 });
    };
    window.addEventListener('bifeai:tour', onStart);
    return () => window.removeEventListener('bifeai:tour', onStart);
  }, [navigate, pathname]);

  // La primera vez que entra el dueño o encargado, se ofrece el recorrido general.
  useEffect(() => {
    if (pathname === '/' && !help.toursSeen.includes('general') && (can('settings.manage') || can('users.manage'))) {
      const t = setTimeout(() => setTour({ id: 'general', i: 0 }), 1200);
      return () => clearTimeout(t);
    }
  }, [pathname, help.toursSeen, can]);

  const def = tour ? TOURS[tour.id] : null;
  const step = def && tour ? def.steps[tour.i] : null;

  const measure = useCallback(() => {
    if (!step?.target) return setRect(null);
    const el = document.querySelector(step.target);
    if (!el) return setRect(null);
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return setRect(null);
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useLayoutEffect(() => {
    if (!step) return;
    const el = step.target ? document.querySelector(step.target) : null;
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    // La pantalla puede tardar un instante en cargar: se mide de nuevo.
    measure();
    const t1 = setTimeout(measure, 350);
    const t2 = setTimeout(measure, 900);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step, measure]);

  const finish = useCallback(() => {
    if (tour && !help.toursSeen.includes(tour.id)) setHelp(me.user.id, { toursSeen: [...help.toursSeen, tour.id] });
    setTour(null);
  }, [tour, help.toursSeen, me.user.id]);

  useEffect(() => {
    if (!tour) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tour, finish]);

  if (!def || !tour || !step) return null;
  const last = tour.i === def.steps.length - 1;
  const pad = 6;
  const hole = rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 } : null;

  // El globo va debajo del elemento si entra; si no, arriba. Siempre dentro de la pantalla.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardW = Math.min(340, vw - 32);
  let cardStyle: CSSProperties;
  if (hole) {
    const below = hole.top + hole.height + 12;
    const top = below + 190 < vh ? below : Math.max(16, hole.top - 12 - 190);
    const left = Math.min(Math.max(16, hole.left), vw - cardW - 16);
    cardStyle = { top, left, width: cardW };
  } else {
    cardStyle = { top: '50%', left: '50%', width: cardW, transform: 'translate(-50%, -50%)' };
  }

  return (
    <div className="bf-tour" role="dialog" aria-modal="true" aria-label={def.title}>
      {hole ? <div className="bf-tour-hole" style={hole} /> : <div className="bf-tour-dim" />}
      <div className="bf-tour-card" style={cardStyle}>
        <div className="bf-tour-head">
          <span className="bf-hint">
            {def.title} · {tour.i + 1} de {def.steps.length}
          </span>
          <button type="button" className="bf-icon-btn" aria-label="Salir del recorrido" onClick={finish}>
            <X />
          </button>
        </div>
        <strong>{step.title}</strong>
        <p>{step.text}</p>
        <div className="bf-tour-actions">
          {tour.i > 0 && (
            <Button size="sm" onClick={() => setTour({ ...tour, i: tour.i - 1 })}>
              Anterior
            </Button>
          )}
          <Button size="sm" variant="primary" autoFocus onClick={() => (last ? finish() : setTour({ ...tour, i: tour.i + 1 }))}>
            {last ? 'Listo' : 'Siguiente'}
          </Button>
        </div>
      </div>
    </div>
  );
}
